import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg } from "@/lib/api";
import { rp, todayISO, addDays, INVOICE_STATUS } from "@/lib/format";
import { Card, Field, MoneyInput, BigButton, GhostButton, Pill } from "@/components/admin/common";
import { AccountSelect, useAccounts } from "@/components/finance/shared";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/admin/DateInput";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const blank = () => ({ booking_id: null, customer_name: "", customer_address: "", customer_email: "", customer_phone: "", issue_date: todayISO(),
  due_date: addDays(todayISO(), 7), items: [{ description: "", qty: 1, price: 0 }], discount: 0, deposit_paid: 0, notes: "", status: "draft" });

const Items = ({ items, onChange }) => {
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`invoice-item-${i}`}>
          <Field label={i === 0 ? "Description" : ""} className="col-span-12 sm:col-span-6"><Input value={it.description} onChange={(e) => upd(i, "description", e.target.value)} className="h-12" data-testid={`invoice-item-desc-${i}`} /></Field>
          <Field label={i === 0 ? "Qty" : ""} className="col-span-3 sm:col-span-1"><Input type="number" min="0" value={it.qty} onChange={(e) => upd(i, "qty", Number(e.target.value))} className="h-12" data-testid={`invoice-item-qty-${i}`} /></Field>
          <Field label={i === 0 ? "Unit price" : ""} className="col-span-7 sm:col-span-4"><MoneyInput value={it.price} onChange={(v) => upd(i, "price", Number(v) || 0)} testid={`invoice-item-price-${i}`} /></Field>
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} disabled={items.length === 1} className="col-span-2 sm:col-span-1 h-12 rounded-xl border border-border grid place-items-center text-slate-500 disabled:opacity-30" aria-label="Remove line" data-testid={`invoice-item-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <GhostButton type="button" className="!h-10" onClick={() => onChange([...items, { description: "", qty: 1, price: 0 }])} data-testid="invoice-add-item-btn"><Plus className="w-4 h-4" />Add line</GhostButton>
    </div>
  );
};

const PayDialog = ({ inv, open, onClose, onPaid }) => {
  const [accounts] = useAccounts();
  const [f, setF] = useState({ date: todayISO(), cash_account: "1-1200" });
  const pay = async () => {
    try {
      const r = await api.post(`/admin/invoices/${inv.id}/pay`, f);
      toast.success("Invoice paid — income recorded in Keuangan");
      onPaid(r.data);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="font-admin" data-testid="invoice-pay-dialog">
        <DialogHeader><DialogTitle>Mark {inv.number} as paid</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Records {rp(inv.balance)} as income.</p>
        <Field label="Received into"><AccountSelect accounts={accounts.filter((a) => a.is_cash)} value={f.cash_account} onChange={(v) => setF({ ...f, cash_account: v })} grouped={false} testid="invoice-pay-account-select" /></Field>
        <Field label="Date"><DateInput value={f.date} onChange={(v) => v && setF((p) => ({ ...p, date: v }))} testid="invoice-pay-date-input" /></Field>
        <BigButton onClick={pay} data-testid="invoice-pay-confirm-btn">Confirm payment</BigButton>
      </DialogContent>
    </Dialog>
  );
};

export default function InvoiceEditor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  useEffect(() => {
    if (!isNew) api.get(`/admin/invoices/${id}`).then((r) => setInv(r.data));
    else if (sp.get("booking")) api.get(`/admin/invoices/from-booking/${sp.get("booking")}`).then((r) => setInv(r.data));
    else setInv(blank());
  }, [id, isNew, sp]);
  if (!inv) return <p className="text-slate-500">Loading…</p>;

  const set = (k) => (v) => setInv((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const subtotal = inv.items.reduce((s, i) => s + (i.qty || 0) * (i.price || 0), 0);
  const total = Math.max(subtotal - (inv.discount || 0), 0);
  const balance = Math.max(total - (inv.deposit_paid || 0), 0);
  const paid = inv.status === "paid";

  const save = async () => {
    if (!inv.customer_name) return toast.error("Customer name is required");
    setBusy(true);
    try {
      const body = { ...inv, discount: Number(inv.discount) || 0, deposit_paid: Number(inv.deposit_paid) || 0 };
      const r = isNew ? await api.post("/admin/invoices", body) : await api.put(`/admin/invoices/${id}`, body);
      toast.success(`Invoice ${r.data.number} saved`);
      if (isNew) navigate(`/admin/invoices/${r.data.id}`, { replace: true });
      else setInv(r.data);
      return r.data;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    try { await api.delete(`/admin/invoices/${id}`); toast.success("Invoice deleted"); navigate("/admin/invoices"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-4xl space-y-4" data-testid="invoice-editor-page">
      <Link to="/admin/invoices" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-ink" data-testid="back-to-invoices"><ArrowLeft className="w-4 h-4" />Invoices</Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-extrabold" data-testid="invoice-number">{inv.number || "New invoice"}</h1>
          <Pill map={INVOICE_STATUS} value={inv.status} testid="invoice-status-pill" />
        </div>
        {!isNew && (
          <div className="flex flex-wrap gap-2">
            <GhostButton onClick={() => download(`/admin/invoices/${id}/pdf`)} data-testid="invoice-download-pdf-btn"><Download className="w-4 h-4" />Download PDF</GhostButton>
            {!paid && <BigButton onClick={() => setPayOpen(true)} className="!bg-emerald-600" data-testid="invoice-mark-paid-btn"><CheckCircle2 className="w-4 h-4" />Mark as paid</BigButton>}
          </div>
        )}
      </div>
      {inv.booking_id && <p className="text-sm">Linked to <Link className="font-semibold text-oceandeep underline" to={`/admin/bookings/${inv.booking_id}`} data-testid="invoice-booking-link">booking</Link></p>}
      <Card className="p-4 sm:p-6 grid sm:grid-cols-2 gap-4">
        <Field label="Customer name *" className="sm:col-span-2"><Input value={inv.customer_name} onChange={set("customer_name")} className="h-12" data-testid="invoice-customer-input" /></Field>
        <Field label="Email"><Input value={inv.customer_email} onChange={set("customer_email")} className="h-12" data-testid="invoice-email-input" /></Field>
        <Field label="Phone"><Input value={inv.customer_phone} onChange={set("customer_phone")} className="h-12" data-testid="invoice-phone-input" /></Field>
        <Field label="Address" className="sm:col-span-2"><Input value={inv.customer_address} onChange={set("customer_address")} className="h-12" data-testid="invoice-address-input" /></Field>
        <Field label="Issue date"><DateInput value={inv.issue_date} onChange={(v) => v && set("issue_date")(v)} testid="invoice-issue-date-input" /></Field>
        <Field label="Due date"><DateInput value={inv.due_date} onChange={set("due_date")} testid="invoice-due-date-input" /></Field>
        {!paid && (
          <Field label="Status">
            <Select value={inv.status} onValueChange={set("status")}>
              <SelectTrigger className="h-12" data-testid="invoice-status-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="sent">Sent</SelectItem></SelectContent>
            </Select>
          </Field>
        )}
      </Card>
      <Card className="p-4 sm:p-6 space-y-4">
        <p className="font-extrabold">Line items (IDR)</p>
        <Items items={inv.items} onChange={set("items")} />
      </Card>
      <Card className="p-4 sm:p-6 grid sm:grid-cols-2 gap-4">
        <Field label="Discount"><MoneyInput value={inv.discount} onChange={set("discount")} testid="invoice-discount-input" /></Field>
        <Field label="Deposit already paid"><MoneyInput value={inv.deposit_paid} onChange={set("deposit_paid")} testid="invoice-deposit-input" /></Field>
        <Field label="Notes (shown on invoice)" className="sm:col-span-2"><Textarea value={inv.notes} onChange={set("notes")} data-testid="invoice-notes-input" /></Field>
        <div className="sm:col-span-2 bg-sand rounded-2xl p-4 space-y-1 text-[15px]" data-testid="invoice-totals">
          <div className="flex justify-between"><span>Subtotal</span><span>{rp(subtotal)}</span></div>
          <div className="flex justify-between"><span>Total</span><span className="font-bold" data-testid="invoice-total">{rp(total)}</span></div>
          <div className="flex justify-between text-lg font-extrabold text-oceandeep"><span>Balance due</span><span data-testid="invoice-balance">{rp(paid ? 0 : balance)}</span></div>
        </div>
      </Card>
      <div className="flex gap-2">
        {!isNew && !paid && <GhostButton onClick={remove} className="text-rose-600" data-testid="invoice-delete-btn"><Trash2 className="w-4 h-4" /></GhostButton>}
        <BigButton onClick={save} disabled={busy} className="flex-1" data-testid="invoice-save-btn">{busy ? "Saving…" : "Save invoice"}</BigButton>
      </div>
      {!isNew && <PayDialog inv={{ ...inv, balance }} open={payOpen} onClose={() => setPayOpen(false)} onPaid={setInv} />}
    </div>
  );
}
